import { getApiErrorDetail, useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Carrier, UpdateCarrier } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { carrierKeys, createCarrierApi } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UpdateCarrierVariables {
  data: UpdateCarrier;
  id: string;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateCarrier(): UseMutationResult<Carrier, Error, UpdateCarrierVariables> {
  const client = useApiClient('admin-auth');
  const api = createCarrierApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<Carrier, Error, UpdateCarrierVariables>({
    mutationFn: ({ data, id }: UpdateCarrierVariables) => api.updateCarrier(id, data),
    onError: (error: Error) => {
      const detail = getApiErrorDetail(error);
      if (detail.errorCode === 'Conflict.DuplicateCarrierName') {
        toast.error(t('common.errorDuplicateName'));
      } else if (detail.errorCode === 'Conflict.DuplicateCarrierCode') {
        toast.error(t('common.errorDuplicateCode'));
      } else {
        toast.error(detail.message || t('spaces.settings.carriers.toast.updateError'));
      }
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: carrierKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: carrierKeys.detail(variables.id) });
      toast.success(t('spaces.settings.carriers.toast.updated'));
    },
  });
}
